import { CartItem, BookingDetails } from '../types';

const CART_STORAGE_KEY = 'guest_food_cart';
const CART_EXPIRY_HOURS = 24; // Cart expires after 24 hours

interface StoredCart {
  items: CartItem[];
  timestamp: number;
  bookingReference: string;
  deliveryLocation?: 'room' | 'restaurant' | 'lobby';
  deliveryInstructions?: string;
  hotelId?: string;
}

/**
 * Cart Storage Utility
 * Handles localStorage persistence for guest food cart
 */
class CartStorage {
  private getStorageKey(bookingReference?: string): string {
    return bookingReference ? `${CART_STORAGE_KEY}_${bookingReference}` : CART_STORAGE_KEY;
  }

  /**
   * Save cart items to localStorage
   */
  saveCart(
    items: CartItem[], 
    booking: BookingDetails,
    deliveryLocation?: 'room' | 'restaurant' | 'lobby',
    deliveryInstructions?: string
  ): void {
    try {
      const cartData: StoredCart = {
        items,
        timestamp: Date.now(),
        bookingReference: booking.reference_number,
        deliveryLocation,
        deliveryInstructions,
        hotelId: booking.hotel.id,
      };

      const storageKey = this.getStorageKey(booking.reference_number);
      localStorage.setItem(storageKey, JSON.stringify(cartData));
      
      console.log('Cart saved to localStorage:', cartData);
    } catch (error) {
      console.warn('Failed to save cart to localStorage:', error);
    }
  }

  /**
   * Load cart items from localStorage
   */
  loadCart(booking: BookingDetails): {
    items: CartItem[];
    deliveryLocation?: 'room' | 'restaurant' | 'lobby';
    deliveryInstructions?: string;
  } {
    try {
      const storageKey = this.getStorageKey(booking.reference_number);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        return { items: [] };
      }

      const cartData: StoredCart = JSON.parse(stored);
      
      // Check if cart has expired
      const age = Date.now() - cartData.timestamp;
      const maxAge = CART_EXPIRY_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds
      
      if (age > maxAge) {
        console.log('Cart expired, removing from storage');
        this.clearCart(booking);
        return { items: [] };
      }

      // Verify booking reference matches
      if (cartData.bookingReference !== booking.reference_number) {
        console.log('Booking reference mismatch, clearing cart');
        this.clearCart(booking);
        return { items: [] };
      }

      // Verify hotel ID matches (if available)
      if (cartData.hotelId && cartData.hotelId !== booking.hotel.id) {
        console.log('Hotel ID mismatch, clearing cart');
        this.clearCart(booking);
        return { items: [] };
      }

      console.log('Cart loaded from localStorage:', cartData);

      return {
        items: cartData.items || [],
        deliveryLocation: cartData.deliveryLocation,
        deliveryInstructions: cartData.deliveryInstructions,
      };
    } catch (error) {
      console.warn('Failed to load cart from localStorage:', error);
      return { items: [] };
    }
  }

  /**
   * Clear cart from localStorage
   */
  clearCart(booking: BookingDetails): void {
    try {
      const storageKey = this.getStorageKey(booking.reference_number);
      localStorage.removeItem(storageKey);
      console.log('Cart cleared from localStorage');
    } catch (error) {
      console.warn('Failed to clear cart from localStorage:', error);
    }
  }

  /**
   * Clear all cart data (useful for cleanup)
   */
  clearAllCarts(): void {
    try {
      // Find all cart-related keys
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CART_STORAGE_KEY)
      );

      keys.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`Cleared ${keys.length} cart(s) from localStorage`);
    } catch (error) {
      console.warn('Failed to clear all carts from localStorage:', error);
    }
  }

  /**
   * Get cart item count for a specific booking
   */
  getCartItemCount(booking: BookingDetails): number {
    const { items } = this.loadCart(booking);
    return items.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Get cart total amount for a specific booking
   */
  getCartTotal(booking: BookingDetails): number {
    const { items } = this.loadCart(booking);
    return items.reduce((total, item) => total + item.total_price, 0);
  }

  /**
   * Check if cart has items
   */
  hasCartItems(booking: BookingDetails): boolean {
    return this.getCartItemCount(booking) > 0;
  }

  /**
   * Cleanup expired carts
   */
  cleanupExpiredCarts(): void {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(CART_STORAGE_KEY)
      );

      let cleanedCount = 0;

      keys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const cartData: StoredCart = JSON.parse(stored);
            const age = Date.now() - cartData.timestamp;
            const maxAge = CART_EXPIRY_HOURS * 60 * 60 * 1000;

            if (age > maxAge) {
              localStorage.removeItem(key);
              cleanedCount++;
            }
          }
        } catch (error) {
          // Invalid data, remove it
          localStorage.removeItem(key);
          cleanedCount++;
        }
      });

      if (cleanedCount > 0) {
        console.log(`Cleaned up ${cleanedCount} expired cart(s)`);
      }
    } catch (error) {
      console.warn('Failed to cleanup expired carts:', error);
    }
  }
}

// Export singleton instance
export const cartStorage = new CartStorage();

// Utility functions
export const saveGuestCart = (
  items: CartItem[], 
  booking: BookingDetails,
  deliveryLocation?: 'room' | 'restaurant' | 'lobby',
  deliveryInstructions?: string
) => {
  cartStorage.saveCart(items, booking, deliveryLocation, deliveryInstructions);
};

export const loadGuestCart = (booking: BookingDetails) => {
  return cartStorage.loadCart(booking);
};

export const clearGuestCart = (booking: BookingDetails) => {
  cartStorage.clearCart(booking);
};

export const getGuestCartCount = (booking: BookingDetails) => {
  return cartStorage.getCartItemCount(booking);
};

// Auto cleanup on module load
cartStorage.cleanupExpiredCarts();